import {
  FC,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {
  getUserByToken,
  logout as logoutApi,
  recordTabClose,
  recordSessionResume,
} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {clearAllAppCookies} from '../../../../Utils/cookieManager'
import {streamsStore} from '../../../../services/streamsStore'
import {ccpLogger} from '../../../../Utils/ccpLogger'
import {clearConnectionTestSessionDismiss} from '../../../UI/connection-test/sessionDismiss'
import {getPendingPreCallGuardrails} from '../../../../services/preCallGuardrailStorage'
import toaster from '../../../../Utils/toaster'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  refreshCurrentUser: (opts?: {force?: boolean}) => Promise<void>
  logout: () => boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  refreshCurrentUser: async () => {},
  logout: () => false,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(() => {
    const stored = localStorage.getItem('currentUser')
    return stored ? JSON.parse(stored) : undefined
  })
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  useEffect(() => {
    console.log('Auth Provider mounted')
  }, [])

  // Tab close: record "logout" on page unload only (beforeunload only — unload would fire too and cause 2 entries)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if ((window as {__logoutInProgress__?: boolean}).__logoutInProgress__) return
      const auth = authHelper.getAuth()
      if (auth?.token) recordTabClose(auth.token)
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Tab reopen / page refresh: record "login" whenever app loads with an existing auth token.
  // Backend dedupe (3s window) prevents duplicate entries when this happens right after a real login.
  useEffect(() => {
    if (!auth?.token) return
    clearConnectionTestSessionDismiss()
    recordSessionResume().catch(() => {})
  }, [auth?.token])

  // Shared throttle so the mount fetch, the visibility fetch, and the day-roll
  // fetch don't pile on top of each other when several fire in quick succession.
  const lastLoggedUserRefreshRef = useRef<number>(0)
  const REFRESH_MIN_INTERVAL_MS = 60_000

  const refreshLoggedUser = useCallback(
    async (opts?: {force?: boolean}) => {
      if (!authHelper.getAuth()?.token) return
      const now = Date.now()
      if (!opts?.force && now - lastLoggedUserRefreshRef.current < REFRESH_MIN_INTERVAL_MS) return
      lastLoggedUserRefreshRef.current = now
      try {
        const {data} = await getUserByToken()
        if (data?.result?.userType === 'LEARNER') {
          logout()
          return
        }
        setCurrentUser(data)
      } catch {
        // Allow another attempt on the next trigger if this one failed.
        lastLoggedUserRefreshRef.current = 0
      }
    },
    [setCurrentUser]
  )

  // Refresh /loggeduser when the tab regains focus. This propagates
  // server-side changes (e.g. an admin granting more AI interpretation
  // attempts) without requiring the user to re-login.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      refreshLoggedUser()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [refreshLoggedUser])

  // External trigger so modules that can't import from this file (to avoid
  // pulling in the heavy auth bundle) can still ask for a force refresh —
  // e.g. PendingFeedbackGate after a feedback submit changes the user's cap.
  useEffect(() => {
    const onExternalRefresh = () => { refreshLoggedUser({force: true}) }
    window.addEventListener('auth:refresh-current-user', onExternalRefresh)
    return () => window.removeEventListener('auth:refresh-current-user', onExternalRefresh)
  }, [refreshLoggedUser])

  // Streak is counted server-side at most once per calendar day, but the SPA must call the API that day.
  // If the tab stays open across midnight (no refresh / relogin), poll the local date and ping once when the day changes.
  const streakDaySyncedRef = useRef<string>(new Date().toDateString())
  useEffect(() => {
    if (!auth?.token) return

    const maybeSyncStreakForNewDay = () => {
      if (!authHelper.getAuth()?.token) return
      const today = new Date().toDateString()
      if (today === streakDaySyncedRef.current) return
      getUserByToken()
        .then(({data}) => {
          setCurrentUser(data)
          streakDaySyncedRef.current = today
        })
        .catch(() => {
          // Leave ref unchanged so the next interval retries after errors / offline.
        })
    }

    const id = window.setInterval(maybeSyncStreakForNewDay, 60_000)
    return () => window.clearInterval(id)
  }, [auth?.token, setCurrentUser])

  // Persist currentUser to localStorage whenever it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser))
    } else {
      localStorage.removeItem('currentUser')
    }
  }, [currentUser])

  // Fetch user details on mount and whenever auth changes. Force-refresh
  // regardless of the throttle so page refresh always pulls the latest
  // /loggeduser (catches server-side limit changes since last visit).
  useEffect(() => {
    if (auth && auth.token) {
      refreshLoggedUser({force: true}).catch(() => setCurrentUser(undefined))
    } else if (!auth) {
      setCurrentUser(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  const logout = (): boolean => {
    if (getPendingPreCallGuardrails().length > 0) {
      window.dispatchEvent(new CustomEvent('precall:show-required'))
      toaster(
        'error',
        'Complete pre-call questions before logging out. The call form has been restored.'
      )
      return false
    }
    ;(window as {__logoutInProgress__?: boolean}).__logoutInProgress__ = true
    const deviceToken = localStorage.getItem('fcm_device_token') || ''

    // Sign out of the CCP first so Amazon Connect sees OFFLINE → /logout → TERMINATE
    // before we drop the portal session. The store's own timeouts bound this; continue
    // on error so a failing CCP logout can't trap the user in the portal.
    streamsStore
      .logout()
      .catch((err) => ccpLogger.warn('Portal logout: CCP logout threw', { error: String(err) }))
      .finally(() => {
        logoutApi(deviceToken)
          .catch(() => {
            // Still clear local state even if backend logout fails (e.g. network)
          })
          .finally(() => {
            saveAuth(undefined)
            setCurrentUser(undefined)
            localStorage.removeItem('currentUser')
            clearAllAppCookies()
            clearConnectionTestSessionDismiss()
            window.location.reload()
          })
      })
    return true
  }

  return (
    <AuthContext.Provider
      value={{auth, saveAuth, currentUser, setCurrentUser, refreshCurrentUser: refreshLoggedUser, logout}}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken()
          if (data) {
            if (data.result?.userType === 'LEARNER') {
              logout()
              return
            }
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.token) {
      requestUser(auth.token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
